import { Typography, Badge } from "@altimateai/lego";
import { TableHeaderType } from "@/components/tables/types";
import dayjs from "dayjs";
import { SubscriptionHistoryItem } from "@/pages/subscriptions/services/types";
import { RunStatus } from "@/pages/subscriptions/constants";

const statusConfig: Record<RunStatus, { className: string; label: string }> = {
  [RunStatus.TRIGGERED]: {
    className: "al-bg-red-50 al-text-red-700 al-border-red-200",
    label: "Triggered",
  },
  [RunStatus.SENT]: {
    className: "al-bg-green-50 al-text-green-700 al-border-green-200",
    label: "Sent",
  },
  [RunStatus.FAILURE]: {
    className: "al-bg-gray-50 al-text-gray-700 al-border-gray-200",
    label: "Failed",
  },
  [RunStatus.SKIPPED]: {
    className: "al-bg-gray-50 al-text-gray-600 al-border-gray-200",
    label: "Skipped",
  },
};

const defaultStatusConfig = {
  className: "al-bg-gray-50 al-text-gray-500 al-border-gray-200",
  label: "Unknown",
};

export const subscriptionDetailColumns: TableHeaderType<SubscriptionHistoryItem>[] = [
  {
    id: 0,
    uid: "status",
    label: "Status",
    width: "12%",
    render: item => {
      const config = statusConfig[item.status as keyof typeof statusConfig] || defaultStatusConfig;

      return (
        <Badge variant="outline" className={config.className}>
          {config.label}
        </Badge>
      );
    },
  },
  {
    id: 1,
    uid: "id",
    label: "ID",
    width: "10%",
    render: item => (
      <Typography variant="body" className="al-text-sm al-font-mono">
        {item.id}
      </Typography>
    ),
  },
  {
    id: 2,
    uid: "executed_at",
    label: "Executed On",
    width: "18%",
    render: item => (
      <Typography variant="body" className="al-text-sm">
        {dayjs(item.executed_at).format("DD MMM YYYY, h:mmA")}
      </Typography>
    ),
  },
  {
    id: 3,
    uid: "message",
    label: "Message",
    width: "60%",
    render: item => {
      // triggered (alert fired) and sent (report delivered) carry a real backend message.
      if (item.status === RunStatus.TRIGGERED || item.status === RunStatus.SENT) {
        return (
          <Typography variant="body" className="al-text-sm">
            {item.message}
          </Typography>
        );
      }
      // Alert ran but threshold not crossed — no message to show.
      if (item.status === RunStatus.SKIPPED) {
        return (
          <Typography variant="body" className="al-text-sm al-text-gray-400 al-italic">
            Condition not met, no message sent
          </Typography>
        );
      }
      // Execution failed — surface backend error.message first, then
      // top-level message, then a generic placeholder.
      if (item.status === RunStatus.FAILURE) {
        return (
          <Typography variant="body" className="al-text-sm al-text-gray-400 al-italic">
            {item.error?.message || item.message || "Execution failed, no message sent"}
          </Typography>
        );
      }
      // Unknown / future status — surface message if present.
      return (
        <Typography variant="body" className="al-text-sm al-text-gray-400 al-italic">
          {item.message || "—"}
        </Typography>
      );
    },
  },
];
